import type { FirstTouch } from "./first-touch";

export const FIRST_TOUCH_PROPERTY_MAX_BYTES = {
  cid: 255,
  landing_page: 2 * 1024,
  referrer: 2 * 1024,
  utm_source: 255,
  utm_medium: 255,
  utm_campaign: 255,
  utm_term: 255,
  utm_content: 255,
  first_seen_at: 64,
} as const satisfies Record<keyof FirstTouch, number>;

export type FirstTouchEventProperties = Partial<FirstTouch>;

/**
 * Clerk unsafe metadata is caller-controlled JSON, not a trusted FirstTouch.
 * Copy only the browser producer's documented fields and drop malformed or
 * oversized values independently so one bad field does not discard useful
 * attribution.
 */
export function sanitizeFirstTouchProperties(value: unknown): FirstTouchEventProperties {
  if (!isRecord(value)) return {};

  const cid = boundedIdentifierString(value.cid, FIRST_TOUCH_PROPERTY_MAX_BYTES.cid);
  const landingPage = boundedString(
    value.landing_page,
    FIRST_TOUCH_PROPERTY_MAX_BYTES.landing_page,
  );
  const referrer = boundedNullableString(value.referrer, FIRST_TOUCH_PROPERTY_MAX_BYTES.referrer);
  const utmSource = boundedNullableString(
    value.utm_source,
    FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_source,
  );
  const utmMedium = boundedNullableString(
    value.utm_medium,
    FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_medium,
  );
  const utmCampaign = boundedNullableString(
    value.utm_campaign,
    FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_campaign,
  );
  const utmTerm = boundedNullableString(value.utm_term, FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_term);
  const utmContent = boundedNullableString(
    value.utm_content,
    FIRST_TOUCH_PROPERTY_MAX_BYTES.utm_content,
  );
  const firstSeenAt = boundedString(
    value.first_seen_at,
    FIRST_TOUCH_PROPERTY_MAX_BYTES.first_seen_at,
  );

  return {
    ...(cid === undefined ? {} : { cid }),
    ...(landingPage === undefined ? {} : { landing_page: landingPage }),
    ...(referrer === undefined ? {} : { referrer }),
    ...(utmSource === undefined ? {} : { utm_source: utmSource }),
    ...(utmMedium === undefined ? {} : { utm_medium: utmMedium }),
    ...(utmCampaign === undefined ? {} : { utm_campaign: utmCampaign }),
    ...(utmTerm === undefined ? {} : { utm_term: utmTerm }),
    ...(utmContent === undefined ? {} : { utm_content: utmContent }),
    ...(firstSeenAt === undefined ? {} : { first_seen_at: firstSeenAt }),
  };
}

function boundedString(value: unknown, maxBytes: number): string | undefined {
  return typeof value === "string" && utf8Bytes(value) <= maxBytes ? value : undefined;
}

function boundedIdentifierString(value: unknown, maxBytes: number): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized && utf8Bytes(normalized) <= maxBytes ? normalized : undefined;
}

function boundedNullableString(value: unknown, maxBytes: number): string | null | undefined {
  return value === null ? null : boundedString(value, maxBytes);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function utf8Bytes(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}
